import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Shield, Gem, Layers, Zap, ArrowRight, Star, Crown, Package, BarChart3, Users, Truck,
  Sparkles, Flag, Film, DollarSign, TrendingUp, Calendar, Award, Fingerprint, Trophy,
  FileCheck, Lock, Repeat, Ticket, Music2, Tv, BookOpen, Gamepad2, Building2, Radio,
  Check, ShieldCheck, CircleDollarSign,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { API } from '../../utils';
import HERO_FALLBACK_DATAURI from '../../assets/heroFallback';

const PARTNERSHIP_EMAIL = 'mailto:partnerships@blkpacks.fun';

/* ── Shared title — uses unified brand gradient. ────────────────────── */
const GradientTitle = ({ children, size = 'md', className = '' }) => {
  const sz = size === 'xl'
    ? 'text-3xl sm:text-4xl md:text-5xl'
    : size === 'lg' ? 'text-2xl md:text-3xl' : 'text-lg md:text-xl';
  return (
    <h2
      className={`font-black tracking-tight ${sz} ${className} brand-gradient-text`}
      style={{ fontFamily: "'Oswald', sans-serif" }}
    >
      {children}
    </h2>
  );
};

const denominations = [
  { label: '1/4', weight: '¼ unit' },
  { label: '1/2', weight: '½ unit' },
  { label: '1', weight: '1 unit' },
  { label: '5', weight: '5 units' },
  { label: '10', weight: '10 units' },
  { label: '25', weight: '25 units' },
  { label: '50', weight: '50 units' },
];

const DenominationTable = ({ metal }) => (
  <div className="mb-5">
    <div className="text-gray-500 text-[10px] font-black tracking-[0.2em] uppercase mb-2">
      7 Denomination Series · {metal} Weight Per Card
    </div>
    <div className="grid grid-cols-7 gap-1.5">
      {denominations.map((d) => (
        <div
          key={d.label}
          className="bg-gray-900/70 border border-white/[0.06] rounded-lg p-1.5 text-center hover:border-orange-500/30 transition-colors"
        >
          <div className="text-white font-black text-sm">{d.label}</div>
          <div className="text-gray-500 text-[8px] leading-tight">{d.weight}</div>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Revenue Projection Chart — NET 60 Day payout ──────────────────── */
const RevenueChart = ({ mintSize, dropPrice, revShare, accentColor = 'indigo', tierLabel }) => {
  const totalRevenue = mintSize * dropPrice;
  const partnerCut = totalRevenue * (revShare / 100);
  const platformCut = totalRevenue - partnerCut;
  const formatK = (n) =>
    n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${(n / 1000).toFixed(0)}K`;
  const partnerPct = revShare;
  const platformPct = 100 - revShare;

  const colors = {
    indigo: { bar: 'bg-orange-500', text: 'text-orange-400', border: 'border-orange-500/25' },
    amber: { bar: 'bg-orange-400', text: 'text-orange-400', border: 'border-orange-500/25' },
    cyan: { bar: 'bg-orange-400', text: 'text-orange-400', border: 'border-orange-500/25' },
  };
  const c = colors[accentColor] || colors.indigo;

  return (
    <div
      className={`bg-black/40 border ${c.border} rounded-xl p-4 mb-5`}
      data-testid={`revenue-chart-${tierLabel}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={14} className={c.text} />
        <span className="text-white font-black text-[11px] uppercase tracking-[0.2em]">
          Sold-Out Revenue Projection
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center">
          <div className="text-gray-500 text-[9px] uppercase tracking-wider">Total Sales</div>
          <div className="text-white font-black text-lg">{formatK(totalRevenue)}</div>
          <div className="text-gray-600 text-[9px]">
            {mintSize.toLocaleString()} × ${dropPrice}
          </div>
        </div>
        <div className="text-center">
          <div className="text-gray-500 text-[9px] uppercase tracking-wider">
            Your Cut ({revShare}%)
          </div>
          <div className={`font-black text-lg ${c.text}`}>{formatK(partnerCut)}</div>
          <div className="text-gray-600 text-[9px]">Paid NET 60 post sell-out</div>
        </div>
        <div className="text-center">
          <div className="text-gray-500 text-[9px] uppercase tracking-wider">
            BLK PACKS ({100 - revShare}%)
          </div>
          <div className="text-gray-400 font-black text-lg">{formatK(platformCut)}</div>
          <div className="text-gray-600 text-[9px]">Production · fulfillment · CS</div>
        </div>
      </div>

      <div className="relative h-8 rounded-lg overflow-hidden bg-gray-900/70 flex">
        <div
          className={`${c.bar} flex items-center justify-center transition-all duration-700`}
          style={{ width: `${partnerPct}%` }}
        >
          <span className="text-black text-[10px] font-black">{revShare}% — YOU</span>
        </div>
        <div className="bg-gray-800/60 flex items-center justify-center flex-1">
          <span className="text-gray-400 text-[10px] font-bold">{platformPct}% Platform</span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 mt-2.5">
        <Calendar size={10} className="text-gray-600" />
        <span className="text-gray-600 text-[9px]">
          Commission wired NET 60 days after the drop is fully sold out — guaranteed processor
          payouts via Apple &amp; Google.
        </span>
      </div>
    </div>
  );
};

/* ─── Platform Inclusions — shows what every package ships with ───────── */
const INCLUSIONS = [
  { icon: Truck, label: 'End-to-End Fulfillment', sub: 'Shipping · returns · customer service handled by BLK PACKS' },
  { icon: BarChart3, label: 'Full Export Data', sub: 'CRM-ready buyer + collector data, real-time analytics dashboard' },
  { icon: Zap, label: 'Odds-Based Drops', sub: 'SEC-compliant rarity disclosure, audit trail per mint' },
  { icon: Building2, label: 'White-Label Platform', sub: 'Your branding, your event pages, your app-in-app experience' },
  { icon: Users, label: 'Dedicated Account Mgmt', sub: 'Senior producer + 3D / design crew assigned to your brand' },
  { icon: ShieldCheck, label: 'Guaranteed Payouts', sub: 'Apple & Google processor clearance backed by BLK PACKS' },
  { icon: Fingerprint, label: 'Serialized Chain of Custody', sub: 'EMERGE public ledger records every card, trade, and rip' },
  { icon: Flag, label: 'Made in America', sub: 'Gold Line · Silver line · domestic production & fulfillment' },
];

const InclusionGrid = ({ accent = 'amber' }) => {
  const ringMap = {
    amber: 'border-orange-500/15 hover:border-orange-500/35',
    cyan: 'border-orange-500/15 hover:border-orange-500/35',
    gray: 'border-white/[0.06] hover:border-white/20',
  };
  const iconMap = { amber: 'text-orange-400', cyan: 'text-orange-400', gray: 'text-white/80' };
  return (
    <div className="mb-5" data-testid="platform-inclusions">
      <div className="text-gray-500 text-[10px] font-black tracking-[0.2em] uppercase mb-2">
        Every Package Includes
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {INCLUSIONS.map((it) => (
          <div
            key={it.label}
            className={`flex items-start gap-2.5 bg-black/40 border ${ringMap[accent]} rounded-lg p-2.5 transition-colors`}
          >
            <div className="w-7 h-7 rounded-md bg-white/[0.03] flex items-center justify-center flex-shrink-0">
              <it.icon size={13} className={iconMap[accent]} />
            </div>
            <div className="min-w-0">
              <div className="text-white font-bold text-xs leading-tight">{it.label}</div>
              <div className="text-gray-500 text-[10px] leading-snug mt-0.5">{it.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Moments Callout — tier-level brand messaging ──────────────────── */
const MomentsCallout = ({ accentColor = 'indigo' }) => {
  const colors = {
    indigo: { border: 'border-orange-500/20', bg: 'bg-orange-500/[0.04]', text: 'text-orange-400' },
    amber: { border: 'border-orange-500/20', bg: 'bg-orange-500/[0.04]', text: 'text-orange-400' },
  };
  const c = colors[accentColor] || colors.indigo;
  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-4 mb-5`} data-testid="moments-callout">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-white/[0.03] flex items-center justify-center flex-shrink-0 mt-0.5">
          <Film className={c.text} size={18} />
        </div>
        <div>
          <h4 className="text-white font-bold text-sm mb-1">Video Moments — Licensed Highlight Derivatives</h4>
          <p className="text-gray-400 text-xs leading-relaxed">
            Premium highlight clips minted as serialized digital collectibles. You supply the footage or our senior producers coordinate with your creative team. Every Moment is a one-of-one derivative with its own serial, tradeable inside the BLK PACKS economy.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ─── 3D AR Card Preview — actual model-viewer pulling hero model ───── */
const AR3DPreview = ({ compact = false }) => {
  const [modelUrl, setModelUrl] = useState(null);
  const [modelUsdz, setModelUsdz] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/ar/hero-model`)
      .then((res) => {
        if (!alive) return;
        const toR2 = (url) => {
          if (url && url.startsWith('/api/ar/model/')) {
            const fn = url.replace('/api/ar/model/', '');
            return `https://media.blkpacks.fun/ar-models/${encodeURIComponent(fn)}`;
          }
          return url;
        };
        if (res.data?.model_url) setModelUrl(toR2(res.data.model_url));
        if (res.data?.model_url_usdz) setModelUsdz(toR2(res.data.model_url_usdz));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const size = compact ? 'w-full h-[260px]' : 'w-full h-[320px] md:h-[380px]';

  return (
    <div className={`relative ${size} rounded-2xl overflow-hidden border border-orange-500/20 bg-gradient-to-br from-orange-900/10 via-[#060610] to-red-900/10`} data-testid="ar-3d-preview">
      <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 bg-black/60 backdrop-blur-md border border-orange-400/30 rounded-full px-2.5 py-1">
        <div className="w-1.5 h-1.5 rounded-full bg-orange-300 animate-pulse" />
        <span className="brand-gradient-text text-[9px] font-black tracking-[0.2em] uppercase">Live 3D · AR Enabled</span>
      </div>
      <div className="absolute top-2 right-2 z-10 bg-orange-500/80 text-black text-[9px] font-black tracking-wider px-2 py-0.5 rounded">
        LEGENDARY
      </div>
      {modelUrl ? (
        <model-viewer
          src={modelUrl}
          {...(modelUsdz ? { 'ios-src': modelUsdz } : {})}
          poster={HERO_FALLBACK_DATAURI}
          reveal="auto"
          autoplay
          auto-rotate
          camera-controls
          touch-action="pan-y"
          rotation-per-second="28deg"
          shadow-intensity="0.9"
          environment-image="neutral"
          exposure="1.15"
          camera-orbit="0deg 75deg 140%"
          min-camera-orbit="auto auto 100%"
          max-camera-orbit="auto auto 300%"
          field-of-view="30deg"
          ar
          ar-scale="auto"
          ar-placement="floor"
          loading="eager"
          crossorigin="anonymous"
          style={{
            width: '100%',
            height: '100%',
            background:
              'radial-gradient(ellipse at center, rgba(249,115,22,0.18) 0%, #000 72%)',
          }}
          data-testid="ar-partnerships-3d-model"
        />
      ) : (
        <img
          src={HERO_FALLBACK_DATAURI}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {/* card-frame chrome */}
      <div className="absolute bottom-0 inset-x-0 px-3 py-2 bg-gradient-to-t from-black/90 to-transparent flex items-center justify-between">
        <div>
          <div className="text-white text-[10px] font-black uppercase tracking-wider">Sample AR Drop</div>
          <div className="text-orange-400/60 text-[9px]">Your 3D model · anchored to physical card</div>
        </div>
        <div className="text-right">
          <div className="text-white text-[9px] font-mono">#001/1000</div>
          <div className="text-orange-400/80 text-[9px] font-bold">Gold Line</div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════ */
/*   TIER CARDS — elegant executive-grade presentation                    */
/* ══════════════════════════════════════════════════════════════════════ */

const TierCardShell = ({ accentBorder, accentBg, testId, children }) => (
  <div className="relative group" data-testid={testId}>
    <div className={`absolute -inset-[1px] ${accentBorder} rounded-3xl blur opacity-40 group-hover:opacity-80 transition-opacity duration-700`} />
    <div className={`relative ${accentBg} rounded-3xl overflow-hidden border border-white/[0.06]`}>
      {children}
    </div>
  </div>
);

const TierHeader = ({ tierLabel, tierName, price, subPrice, Icon, accentText, iconBg }) => (
  <div className="relative px-6 md:px-8 pt-7 pb-5 border-b border-white/[0.05] overflow-hidden">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-24 bg-white/[0.03] rounded-full blur-3xl pointer-events-none" />
    <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl ${iconBg || 'bg-white/[0.04]'} border border-white/10 flex items-center justify-center`}>
          <Icon className={accentText || 'text-white'} size={22} />
        </div>
        <div>
          <span className={`${accentText || 'text-white/40'} text-[10px] font-black tracking-[0.3em] uppercase`}>{tierLabel}</span>
          <h3 className="text-white font-black text-2xl tracking-tight leading-none" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {tierName}
          </h3>
        </div>
      </div>
      <div className="md:text-right">
        <div className="text-gray-500 text-[10px] font-black tracking-widest uppercase">Starting at</div>
        <div className="text-white font-black text-3xl leading-none" style={{ fontFamily: "'Oswald', sans-serif" }}>
          <span className="brand-gradient-text">{price}</span>
        </div>
        {subPrice && <div className="text-gray-600 text-[10px] mt-0.5">{subPrice}</div>}
      </div>
    </div>
  </div>
);

/* ─── Silver Standard — $50K ─── */
const SilverTier = () => (
  <TierCardShell
    testId="partnership-silver-tier"
    accentBorder="bg-gradient-to-r from-gray-300/30 via-white/15 to-gray-300/30"
    accentBg="bg-[#0a0a12]"
  >
    <TierHeader
      tierLabel="Tier 1"
      tierName="SILVER STANDARD"
      price="$50K"
      subPrice="Mint 5,000 · Drop $150"
      Icon={Shield}
      accentText="text-gray-300"
    />
    <div className="px-6 md:px-8 pt-6">
      <p className="text-gray-400 text-sm leading-relaxed mb-4">
        Launch with <span className="text-white font-semibold">Silver line</span> collectible cards paired with digital <span className="text-white font-semibold">Video Moments</span>. Every card tied to live silver spot pricing inside the app — fans track value in real time. Serialized across <span className="text-white font-semibold">4 rarity tiers</span>: Common · Rare · Epic · Legendary.
      </p>
      <MomentsCallout accentColor="indigo" />
      <DenominationTable metal="Silver" />
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { v: '5,000', l: 'Mint Series' },
          { v: '$150', l: 'Drop Price' },
          { v: '4', l: 'Rarity Tiers' },
          { v: '25%', l: 'Rev Share' },
        ].map((s, i) => (
          <div key={i} className="bg-gray-900/70 rounded-lg p-2.5 text-center border border-white/[0.04]">
            <div className="text-white font-black text-lg">{s.v}</div>
            <div className="text-gray-500 text-[9px] uppercase tracking-wider">{s.l}</div>
          </div>
        ))}
      </div>
      <RevenueChart mintSize={5000} dropPrice={150} revShare={25} accentColor="indigo" tierLabel="silver" />
      <InclusionGrid accent="gray" />
    </div>
  </TierCardShell>
);

/* ─── Gold Standard — $100K ─── */
const GoldTier = () => (
  <TierCardShell
    testId="partnership-gold-tier"
    accentBorder="bg-gradient-to-r from-orange-500/50 via-yellow-300/30 to-orange-500/50"
    accentBg="bg-gradient-to-br from-[#0f0c05] via-[#0a0a0a] to-[#0f0c05]"
  >
    <TierHeader
      tierLabel="Tier 2"
      tierName="GOLD STANDARD"
      price="$100K"
      subPrice="Mint 10,000 · Drop $250"
      Icon={Crown}
      accentText="text-orange-400"
    />
    <div className="px-6 md:px-8 pt-6">
      <p className="text-gray-400 text-sm leading-relaxed mb-4">
        The prestige package. Every card <span className="text-orange-400 font-semibold">Gold line</span>, paired with premium <span className="text-orange-400 font-semibold">Moment derivatives</span>. Exclusively <span className="text-orange-400 font-semibold">Legendary drops</span> — your fans get the rarest pulls, tied to live gold spot pricing in the calculator.
      </p>
      <MomentsCallout accentColor="amber" />
      <DenominationTable metal="Gold" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-5">
        {[
          { icon: Gem, label: 'Gold Line', sub: 'Premium collectible cards' },
          { icon: Star, label: 'Legendary', sub: 'All drops maxed' },
          { icon: Layers, label: '10,000 Mint', sub: '7 denominations' },
          { icon: BarChart3, label: '30% Share', sub: 'Paid NET 60' },
        ].map((f, i) => (
          <div key={i} className="bg-orange-500/[0.04] border border-orange-500/15 rounded-xl p-3.5 text-center">
            <f.icon className="text-orange-400 mx-auto mb-1.5" size={18} />
            <div className="text-white font-bold text-sm">{f.label}</div>
            <div className="text-orange-500/40 text-[9px] uppercase tracking-wider">{f.sub}</div>
          </div>
        ))}
      </div>
      <RevenueChart mintSize={10000} dropPrice={250} revShare={30} accentColor="amber" tierLabel="gold" />
      <InclusionGrid accent="amber" />
    </div>
  </TierCardShell>
);

/* ─── AR Silver — $100K ─── */
const ARSilverTier = () => (
  <TierCardShell
    testId="partnership-ar-silver-tier"
    accentBorder="bg-gradient-to-r from-orange-500/40 via-red-500/25 to-orange-500/40"
    accentBg="bg-[#060610]"
  >
    <TierHeader
      tierLabel="Tier 3 · AR Enhanced"
      tierName="AR SILVER EDITION"
      price="$100K"
      subPrice="Mint 5,000 · Drop $250"
      Icon={Zap}
      accentText="text-orange-400"
    />
    <div className="grid md:grid-cols-2 gap-0">
      <div className="px-6 md:px-8 pt-6">
        <p className="text-gray-400 text-sm leading-relaxed mb-4">
          <span className="text-orange-400 font-semibold">Silver Line</span> cards with <span className="text-orange-400 font-semibold">fully custom 3D AR experiences</span>. Fans scan the physical card; a bespoke 3D model pops out and anchors to real space. This is the next-generation collectible format brand fans share and re-engage with on repeat.
        </p>
        <DenominationTable metal="Silver" />
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { v: '5,000', l: 'Mint Series' },
            { v: '$250', l: 'Drop Price' },
            { v: '3D AR', l: 'Every Card' },
            { v: '25%', l: 'Rev Share' },
          ].map((s, i) => (
            <div key={i} className="bg-gray-900/70 rounded-lg p-2.5 text-center border border-white/[0.04]">
              <div className="text-white font-black text-lg">{s.v}</div>
              <div className="text-gray-500 text-[9px] uppercase tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
        <RevenueChart mintSize={5000} dropPrice={250} revShare={25} accentColor="cyan" tierLabel="ar-silver" />
      </div>
      <div className="p-5 md:p-6 flex items-center justify-center bg-gradient-to-br from-orange-900/10 via-[#06060e] to-red-900/10">
        <AR3DPreview compact />
      </div>
    </div>
    <div className="px-6 md:px-8 pb-6">
      <InclusionGrid accent="cyan" />
    </div>
  </TierCardShell>
);

/* ─── AR Gold — $200K (pinnacle) ─── */
const ARGoldTier = () => (
  <TierCardShell
    testId="partnership-ar-gold-tier"
    accentBorder="bg-gradient-to-r from-orange-500/60 via-yellow-300/40 to-orange-500/60"
    accentBg="bg-gradient-to-br from-[#0f0d05] via-[#0a0a0a] to-[#0f0d05]"
  >
    <div className="relative px-6 md:px-8 pt-8 pb-5 text-center overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-24 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/30 rounded-full px-4 py-1 mb-4">
          <Crown className="text-orange-400" size={14} />
          <span className="brand-gradient-text text-[10px] font-black tracking-[0.3em] uppercase">Tier 4 · The Pinnacle</span>
        </div>
        <h3 className="text-white font-black text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
          AR <span className="brand-gradient-text">GOLD</span> EDITION
        </h3>
        <div className="font-black text-4xl mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
          <span className="brand-gradient-text">$200K</span>
        </div>
        <p className="text-gray-500 text-[11px] mb-0">Mint 10,000 · Drop $350 · 30% Revenue Share</p>
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-0 border-t border-white/[0.05]">
      <div className="p-6 md:p-8">
        <p className="text-gray-400 text-sm leading-relaxed mb-4">
          <span className="text-orange-400 font-semibold">Gold Line cards</span> fused with <span className="text-orange-400 font-semibold">bespoke 3D AR sculpting</span> and animated experiences. Maximum prestige, maximum engagement, maximum revenue — the top-tier activation for IP holders ready to define the category.
        </p>
        <DenominationTable metal="Gold" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-orange-500/10 rounded-xl overflow-hidden mb-5">
          {[
            { icon: Gem, label: 'Gold Line', sub: 'Premium collectible cards' },
            { icon: Sparkles, label: 'Custom 3D AR', sub: 'Bespoke per drop' },
            { icon: Star, label: 'Legendary Only', sub: 'All drops maxed' },
            { icon: Layers, label: '10,000 Mint', sub: '7 denominations' },
            { icon: BarChart3, label: '30% Rev Share', sub: 'Paid NET 60' },
            { icon: Flag, label: 'Made in USA', sub: 'Domestic pipeline' },
          ].map((f, i) => (
            <div key={i} className="bg-[#0a0a0a] p-3.5 text-center">
              <f.icon className="text-orange-400 mx-auto mb-1.5" size={18} />
              <div className="text-white font-bold text-sm">{f.label}</div>
              <div className="text-orange-500/40 text-[9px] uppercase tracking-wider">{f.sub}</div>
            </div>
          ))}
        </div>
        <RevenueChart mintSize={10000} dropPrice={350} revShare={30} accentColor="amber" tierLabel="ar-gold" />
      </div>
      <div className="p-5 md:p-6 flex items-center justify-center bg-gradient-to-br from-orange-900/10 via-[#0a0a0a] to-orange-900/10 border-l border-white/[0.04]">
        <AR3DPreview />
      </div>
    </div>

    <div className="px-6 md:px-8 pb-6">
      <InclusionGrid accent="amber" />
    </div>

    <div className="bg-gradient-to-r from-orange-500/10 via-orange-500/5 to-orange-500/10 px-6 md:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-orange-500/15">
      <div className="text-gray-400 text-[11px] text-center sm:text-left max-w-md">
        Ready for the pinnacle? We'll scope your drop, produce your 3D assets, and run a branded EVENT MODE activation alongside launch.
      </div>
      <a
        href={PARTNERSHIP_EMAIL + '?subject=AR%20Gold%20Tier%20Inquiry'}
        className="flex-shrink-0 brand-gradient-btn font-black px-6 py-2.5 rounded-full text-sm hover:shadow-lg hover:shadow-orange-500/40"
        data-testid="ar-gold-inquire-btn"
      >
        Inquire <ArrowRight size={14} />
      </a>
    </div>
  </TierCardShell>
);

/* ══════════════════════════════════════════════════════════════════════ */
/*   EVENT MODE — Live branded TRADING events                             */
/* ══════════════════════════════════════════════════════════════════════ */

const EventModeSection = () => {
  // Simulated live feed — rotates synthetic "trader" activity to make
  // preview boards feel popping even before real brand events are live.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 3500);
    return () => clearInterval(id);
  }, []);

  const FEED_POOL = [
    { handle: '@goldenVault_88', action: 'traded 3 Moments', pts: '+42', prize: 'Legendary Gold Card' },
    { handle: '@pinCollector_99', action: 'scored a 1/1 pin card', pts: '+85', prize: 'Premiere Ticket' },
    { handle: '@studioSam', action: 'closed 5 park trades', pts: '+60', prize: 'VIP Meet & Greet' },
    { handle: '@mouseHouse_21', action: 'swapped Epic for Legendary', pts: '+120', prize: 'Exclusive Event Pack' },
    { handle: '@ticketTrader', action: 'listed 6 cards to floor', pts: '+35', prize: 'Silver Event Card' },
    { handle: '@inkedRoyal', action: 'completed rare-set trade', pts: '+95', prize: 'Lounge Pass' },
    { handle: '@queenOfTrade', action: 'won 2 swap races', pts: '+78', prize: 'Legendary Gold Card' },
    { handle: '@velvetCape', action: 'traded 1 Legendary for 3 Epics', pts: '+110', prize: 'Exclusive Event Pack' },
  ];

  const topRows = [
    { rank: 1, ...FEED_POOL[(tick + 0) % FEED_POOL.length] },
    { rank: 2, ...FEED_POOL[(tick + 1) % FEED_POOL.length] },
    { rank: 3, ...FEED_POOL[(tick + 2) % FEED_POOL.length] },
    { rank: 4, ...FEED_POOL[(tick + 3) % FEED_POOL.length] },
    { rank: 5, ...FEED_POOL[(tick + 4) % FEED_POOL.length] },
  ];

  const liveEmerge = [
    { user: topRows[0].handle, evt: 'TRADE', detail: 'Gave 2× Epic · got 1× Legendary', age: '2s' },
    { user: topRows[1].handle, evt: 'RIP', detail: 'Pulled a 1/250 event card', age: '4s' },
    { user: topRows[2].handle, evt: 'LIST', detail: 'Floor listing @ 180 Pack Points', age: '7s' },
    { user: topRows[3].handle, evt: 'TRADE', detail: 'Completed 3-for-1 trade', age: '11s' },
  ];

  return (
    <div
      className="relative bg-gradient-to-br from-orange-900/20 via-[#0a0a14] to-orange-900/15 border border-orange-500/20 rounded-2xl p-6 md:p-8 mb-6 overflow-hidden"
      data-testid="event-mode-hero"
    >
      <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-orange-500/[0.06] rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center">
            <Ticket className="text-orange-400" size={22} />
          </div>
          <div>
            <GradientTitle size="md">EVENT MODE · LIVE BRAND TRADING</GradientTitle>
            <p className="text-orange-400/70 text-[10px] font-black tracking-[0.22em] uppercase mt-0.5">Your event · your floor · your prize pool</p>
          </div>
        </div>

        <p className="text-gray-300 text-sm leading-relaxed mb-5">
          Launch a <span className="brand-gradient-text font-semibold">branded live event window</span> — a theme-park day, a premiere night, an album release, a playoff run, a convention booth. Inside the window, your fans <span className="brand-gradient-text font-semibold">rip packs, list on the floor, and trade cards head-to-head</span>. Every trade streams into a live leaderboard. <span className="brand-gradient-text font-semibold">Top traders win brand-funded prizes</span> — exclusive event packs, VIP access, physical merch, meet-and-greets. You fund it, we run it, the EMERGE ledger records it.
        </p>

        {/* Theme-park-tier use case spotlight */}
        <div className="bg-orange-500/[0.06] border border-orange-500/25 rounded-xl p-4 mb-5" data-testid="event-mode-disneyland-example">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles className="brand-gradient-text" size={18} />
            </div>
            <div>
              <h4 className="text-white font-bold text-sm mb-1">Example · "Theme-Park Trade Day"</h4>
              <p className="text-gray-400 text-xs leading-relaxed">
                Fans already trade physical pins at park events all day long. BLK PACKS upgrades that ritual into a <span className="brand-gradient-text font-semibold">licensed, serialized, tradeable digital-card economy</span> — one scan at the gate activates Event Mode inside the app. Fans collect, list, and trade limited character cards across the park. The <span className="brand-gradient-text font-semibold">top trader of each day wins an exclusive event-only Legendary pack</span>; top 10 of the week win VIP lounge access + a physical premium card mailed home. Brand-funded, platform-executed, zero lift on your ops team.
              </p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          <div className="bg-orange-500/[0.05] border border-orange-500/20 rounded-xl p-4">
            <Repeat className="text-orange-400 mb-2" size={20} />
            <h4 className="text-white font-bold text-sm mb-1">Live Trade Floor</h4>
            <p className="text-gray-500 text-xs leading-relaxed">Fans list, offer, counter, and accept in real time. Event-window-only listings, branded UI overlays, rarity-locked assets tied to your IP.</p>
          </div>
          <div className="bg-orange-500/[0.05] border border-orange-500/20 rounded-xl p-4">
            <Trophy className="text-orange-400 mb-2" size={20} />
            <h4 className="text-white font-bold text-sm mb-1">Top-Trader Leaderboard</h4>
            <p className="text-gray-500 text-xs leading-relaxed">Daily + weekly rankings. Ranks driven by trade volume, rare-card acquisitions, and collection completion — surfaced inside the app with live push updates.</p>
          </div>
          <div className="bg-orange-500/[0.05] border border-orange-500/20 rounded-xl p-4">
            <Gem className="text-orange-400 mb-2" size={20} />
            <h4 className="text-white font-bold text-sm mb-1">Exclusive Event Rewards</h4>
            <p className="text-gray-500 text-xs leading-relaxed">Brand-funded prize pool: event-only Legendary packs, VIP meet-and-greets, physical merch, signed cards. Distributed via EMERGE with full audit trail.</p>
          </div>
        </div>

        {/* LIVE LEADERBOARD + EMERGE FEED — side by side */}
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-3" data-testid="event-mode-live-dashboard">
          {/* Leaderboard */}
          <div className="bg-black/50 border border-orange-500/25 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-orange-500/20 bg-gradient-to-r from-orange-500/15 via-transparent to-transparent flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                <span className="brand-gradient-text text-[10px] font-black tracking-[0.22em] uppercase">Top Traders · Live</span>
              </div>
              <span className="text-orange-400/60 text-[9px] font-black tracking-wider uppercase">Theme-Park Trade Day · Day 3 of 7</span>
            </div>
            <div className="divide-y divide-amber-500/10">
              {topRows.map((row) => (
                <div key={row.rank} className="px-4 py-2.5 flex items-center gap-3 hover:bg-orange-500/[0.05] transition-colors">
                  <div
                    className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-black flex-shrink-0 ${
                      row.rank === 1
                        ? 'bg-orange-400 text-black shadow-[0_0_12px_-2px_rgba(251,191,36,0.8)]'
                        : row.rank === 2
                          ? 'bg-gray-300 text-black'
                          : row.rank === 3
                            ? 'bg-red-700 text-white'
                            : 'bg-white/5 text-gray-400'
                    }`}
                  >
                    {row.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-bold text-sm truncate">{row.handle}</div>
                    <div className="text-gray-500 text-[10px] truncate">{row.action} · <span className="text-orange-400 font-semibold">{row.pts} pts</span></div>
                  </div>
                  <div className="hidden sm:block flex-shrink-0 text-right">
                    <div className="text-orange-400 font-black text-[9px] uppercase tracking-widest">Today's Prize</div>
                    <div className="text-gray-400 text-[10px]">{row.prize}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 border-t border-orange-500/15 bg-black/60 text-center">
              <span className="text-gray-500 text-[9px] uppercase tracking-[0.22em] font-black">Preview · rotates every 3.5s</span>
            </div>
          </div>

          {/* EMERGE Live Feed */}
          <div className="bg-black/50 border border-orange-500/25 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-orange-500/20 bg-gradient-to-r from-orange-500/15 via-transparent to-transparent flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-orange-300 animate-pulse" />
              <span className="brand-gradient-text text-[10px] font-black tracking-[0.22em] uppercase">EMERGE · Live Ledger</span>
            </div>
            <div className="divide-y divide-amber-500/10">
              {liveEmerge.map((e, i) => (
                <div key={i} className="px-4 py-2.5">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="brand-gradient-text text-[10px] font-black tracking-widest uppercase">{e.evt}</span>
                    <span className="text-gray-500 text-[9px] font-mono">{e.age} ago</span>
                  </div>
                  <div className="text-white text-xs truncate">{e.user}</div>
                  <div className="text-gray-500 text-[10px] truncate">{e.detail}</div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 border-t border-orange-500/15 bg-black/60 text-center">
              <Link to="/emerge" className="text-orange-400/70 hover:brand-gradient-text text-[9px] uppercase tracking-[0.22em] font-black">
                View EMERGE ledger →
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><Lock size={11} className="text-orange-400" /> Anti-Cheat Protected</span>
          <span className="flex items-center gap-1"><FileCheck size={11} className="text-orange-400" /> Audit-Ready Reporting</span>
          <span className="flex items-center gap-1"><Users size={11} className="text-orange-400" /> Push + Email Outreach</span>
          <span className="flex items-center gap-1"><BarChart3 size={11} className="text-orange-400" /> Real-Time Analytics</span>
          <span className="flex items-center gap-1"><Sparkles size={11} className="text-orange-400" /> Populated Floor Protocol</span>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════ */
/*   PAGE                                                                  */
/* ══════════════════════════════════════════════════════════════════════ */

const PartnershipsPage = () => (
  <div className="min-h-screen bg-[#070711] text-white">
    <div className="max-w-5xl mx-auto px-4 py-12 md:py-20">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-xs text-gray-500 mb-6">
        <Link to="/" className="hover:text-white transition-colors whitespace-nowrap">Home</Link>
        <span className="text-gray-600">/</span>
        <Link to="/terms" className="hover:text-white transition-colors whitespace-nowrap">Legal</Link>
        <span className="text-gray-600">/</span>
        <span className="text-white whitespace-nowrap">Partnerships</span>
      </div>

      {/* ═══ ENTERPRISE HERO ═══ */}
      <div className="relative mb-10 md:mb-14 overflow-hidden" data-testid="partnerships-enterprise-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-orange-500/[0.06] rounded-full blur-[160px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[300px] bg-orange-500/[0.04] rounded-full blur-[140px]" />
        </div>

        <div className="relative">
          {/* Authority chips */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            {[
              { icon: Shield, label: 'Official Licensing Platform', accent: 'amber' },
              { icon: Flag, label: 'Made in America' },
              { icon: Fingerprint, label: 'Serialized Chain of Custody' },
              { icon: FileCheck, label: 'EMERGE Public Ledger' },
            ].map((b, i) => (
              <div
                key={i}
                className={`inline-flex items-center gap-2 ${
                  b.accent === 'amber'
                    ? 'bg-orange-500/10 border-orange-500/30'
                    : 'bg-white/[0.03] border-white/10'
                } border rounded-full px-3.5 py-1.5`}
              >
                <b.icon size={12} className={b.accent === 'amber' ? 'text-orange-400' : 'text-white/70'} />
                <span
                  className={`text-[10px] font-black tracking-[0.22em] uppercase ${
                    b.accent === 'amber' ? 'brand-gradient-text' : 'text-white/80'
                  }`}
                >
                  {b.label}
                </span>
              </div>
            ))}
          </div>

          <h1 className="text-center font-black tracking-tight leading-[1.05] mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
            <span className="block text-white text-3xl sm:text-4xl md:text-6xl">LICENSE YOUR IP.</span>
            <span className="block brand-gradient-text text-3xl sm:text-4xl md:text-6xl">OWN THE FAN ECONOMY.</span>
          </h1>

          <p className="text-center text-gray-400 text-sm md:text-base max-w-3xl mx-auto leading-relaxed mb-8 px-2">
            BLK PACKS is the turnkey platform that converts your brand's existing fan engagement into a
            <span className="text-white font-semibold"> licensed, serialized, revenue-generating collectible line</span> —
            Gold line and Silver line collectibles, immersive 3D AR, and live brand-funded <span className="text-white font-semibold">Event Mode</span> trading activations. Built for studios, leagues, music labels, and IP holders ready to make it official.
          </p>

          {/* KPI BAR — elegant gradient cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto mb-8" data-testid="partnerships-kpi-bar">
            {[
              { v: '25–30%', label: 'Revenue Share', sub: 'Per sold-out drop', gradient: 'from-orange-500/20 via-orange-500/10 to-transparent', border: 'border-orange-500/30', text: 'text-orange-400' },
              { v: '$50K → $3.5M', label: 'Tier Range', sub: 'Silver → AR Gold', gradient: 'from-white/[0.06] via-white/[0.02] to-transparent', border: 'border-white/15', text: 'text-white' },
              { v: 'NET 60', label: 'Payout Terms', sub: 'After full sell-out', gradient: 'from-emerald-500/15 via-emerald-500/5 to-transparent', border: 'border-orange-500/25', text: 'brand-gradient-text' },
              { v: '100%', label: 'IP Ownership', sub: 'Retained by you', gradient: 'from-orange-500/15 via-orange-500/5 to-transparent', border: 'border-orange-500/25', text: 'text-orange-400' },
            ].map((k, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br ${k.gradient} border ${k.border} rounded-xl p-4 text-center backdrop-blur-sm`}
              >
                <div
                  className={`font-black text-xl sm:text-2xl md:text-[26px] leading-none ${k.text}`}
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {k.v}
                </div>
                <div className="text-white/80 text-[10px] font-black tracking-[0.18em] uppercase mt-2">{k.label}</div>
                <div className="text-white/40 text-[9px] mt-0.5">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Guarantees strip */}
          <div className="max-w-4xl mx-auto mb-8 bg-black/40 border border-white/[0.08] rounded-xl p-4">
            <div className="text-center text-[10px] brand-gradient-text font-black tracking-[0.25em] uppercase mb-3 flex items-center justify-center gap-2">
              <ShieldCheck size={12} /> BLK PACKS guarantees
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              {[
                { icon: CircleDollarSign, label: 'Apple &amp; Google processor payouts' },
                { icon: Truck, label: 'Shipping, returns &amp; fulfillment' },
                { icon: Users, label: 'Customer service &amp; retention' },
                { icon: FileCheck, label: 'Odds &amp; compliance reporting' },
              ].map((g, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-md bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                    <g.icon size={13} className="brand-gradient-text" />
                  </div>
                  <span
                    className="text-gray-300 text-[11px] leading-snug text-left"
                    dangerouslySetInnerHTML={{ __html: g.label }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
            <a
              href={PARTNERSHIP_EMAIL + '?subject=Partnership%20Deck%20Request'}
              className="w-full sm:w-auto brand-gradient-btn font-black px-8 py-3.5 rounded-full text-sm hover:shadow-lg hover:shadow-orange-500/40 hover:-translate-y-0.5"
              data-testid="hero-request-deck-btn"
            >
              Request Partnership Deck <ArrowRight size={15} />
            </a>
            <a
              href="https://calendly.com/admin-blkpacks/30min?back=1&month=2026-04"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-white/[0.04] border border-white/15 text-white font-bold px-6 py-3.5 rounded-full text-sm hover:bg-white/[0.08] hover:border-white/25 transition-all inline-flex items-center justify-center gap-2"
              data-testid="hero-schedule-call-btn"
            >
              <Calendar size={14} /> Schedule Intro Call
            </a>
          </div>

          {/* Built For — icon grid */}
          <div className="pt-8 border-t border-white/[0.05]">
            <div className="text-center text-[10px] text-gray-500 font-black tracking-[0.3em] uppercase mb-5">
              Built For Licensing Teams At
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="partnerships-built-for">
              {[
                { icon: Trophy, label: 'Sports Franchises' },
                { icon: Music2, label: 'Music Labels' },
                { icon: Film, label: 'Film & TV Studios' },
                { icon: Tv, label: 'Streaming Platforms' },
                { icon: BookOpen, label: 'Publishing Houses' },
                { icon: Gamepad2, label: 'Gaming &amp; IP' },
              ].map((b, i) => (
                <div
                  key={i}
                  className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-3 text-center hover:bg-white/[0.04] hover:border-orange-500/20 transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500/15 to-orange-500/5 border border-orange-500/20 mx-auto mb-2 flex items-center justify-center">
                    <b.icon size={16} className="text-orange-400" />
                  </div>
                  <div
                    className="text-white/80 text-[11px] font-bold leading-tight"
                    dangerouslySetInnerHTML={{ __html: b.label }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto" data-testid="partnerships-how-it-works">
            {[
              { n: '01', title: 'License IP', sub: 'Sign a simple MOU covering likeness, images, and catalog.', icon: FileCheck },
              { n: '02', title: 'Build the Drop', sub: 'Our senior producers ship cards, Moments, and 3D AR.', icon: Layers },
              { n: '03', title: 'Launch + Event Mode', sub: 'Go live with a branded trade-floor activation.', icon: Ticket },
              { n: '04', title: 'Get Paid', sub: 'Revenue share wired NET 60 after full sell-out.', icon: DollarSign },
            ].map((s) => (
              <div
                key={s.n}
                className="bg-[#0a0a14] border border-white/[0.06] rounded-xl p-4 hover:border-orange-500/25 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-orange-400/50 font-black text-xs tracking-widest">{s.n}</div>
                  <s.icon size={14} className="text-orange-400/70" />
                </div>
                <div className="text-white font-bold text-sm mb-0.5">{s.title}</div>
                <div className="text-gray-500 text-[10px] leading-relaxed">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ MOMENTS — Core product ═══ */}
      <div className="bg-gradient-to-br from-orange-900/20 via-orange-900/10 to-orange-900/20 rounded-2xl p-6 md:p-8 mb-6" data-testid="moments-hero">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center">
            <Film className="text-orange-400" size={22} />
          </div>
          <div>
            <GradientTitle size="md">MOMENTS · VIDEO HIGHLIGHT COLLECTIBLES</GradientTitle>
            <p className="text-orange-400/60 text-[10px] font-black tracking-[0.22em] uppercase mt-0.5">
              Core of every drop
            </p>
          </div>
        </div>
        <p className="text-gray-300 text-sm leading-relaxed mb-5">
          Premium highlight clips — from your shows, seasons, catalog, or archive — minted as unique serialized digital collectibles. <span className="brand-gradient-text font-semibold">One-of-one derivatives</span> your fans own, trade, and show off inside the BLK PACKS economy.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { icon: Film, title: 'You supply the footage', sub: 'Highlights, behind-the-scenes, iconic moments — we turn them into premium Moments.' },
            { icon: Users, title: 'Or we produce with you', sub: 'Senior producers + design crew collaborate directly with your creative team.' },
            { icon: Fingerprint, title: 'Serialized forever', sub: 'Unique numbers, EMERGE-verified, tradeable across every drop you launch.' },
          ].map((f) => (
            <div key={f.title} className="bg-orange-500/[0.04] border border-orange-500/15 rounded-xl p-4">
              <f.icon className="text-orange-400 mb-2" size={20} />
              <h4 className="text-white font-bold text-sm mb-1">{f.title}</h4>
              <p className="text-gray-500 text-xs leading-relaxed">{f.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ 3D AR — with real model-viewer ═══ */}
      <div className="bg-gradient-to-br from-orange-900/20 via-[#0a0a14] to-red-900/15 border border-orange-500/15 rounded-2xl p-6 md:p-8 mb-6 overflow-hidden" data-testid="ar-engagement-hero">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center">
            <Sparkles className="text-orange-400" size={22} />
          </div>
          <div>
            <GradientTitle size="md">3D AR · NEXT-LEVEL FAN ENGAGEMENT</GradientTitle>
            <p className="text-orange-400/60 text-[10px] font-black tracking-[0.22em] uppercase mt-0.5">Interactive collectibles fans repeat</p>
          </div>
        </div>

        <div className="grid md:grid-cols-[1.1fr_1fr] gap-5 items-center">
          <div>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              Fans scan a physical card with their phone and a <span className="brand-gradient-text font-semibold">custom 3D model pops out and anchors in real space</span>. Sculpted art, animated effects, interactive layers — all triggered from a card they hold in their hand. Unmatched repeat-engagement metrics across sports, music, and entertainment IP.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div className="bg-orange-500/[0.05] border border-orange-500/20 rounded-xl p-3.5">
                <Sparkles className="text-orange-400 mb-1.5" size={18} />
                <div className="text-white font-bold text-sm">Bespoke 3D Sculpting</div>
                <div className="text-gray-500 text-xs leading-relaxed">Senior 3D artists model per-drop assets tied to your IP.</div>
              </div>
              <div className="bg-orange-500/[0.05] border border-orange-500/20 rounded-xl p-3.5">
                <TrendingUp className="text-orange-400 mb-1.5" size={18} />
                <div className="text-white font-bold text-sm">Measurable ROI</div>
                <div className="text-gray-500 text-xs leading-relaxed">Scan counts, session length, and share-outs piped to your dashboard.</div>
              </div>
            </div>
            <div className="text-[10px] text-gray-500 italic">
              Tap the preview · rotate · tap the AR cube on iOS/Android to launch full AR.
            </div>
          </div>
          <AR3DPreview />
        </div>
      </div>

      {/* ═══ EVENT MODE ═══ */}
      <EventModeSection />

      {/* ═══ OWNERSHIP (single, top-level — no more per-tier repeats) ═══ */}
      <div className="bg-[#0d0d18] border border-white/[0.06] rounded-2xl p-6 md:p-8 mb-12" data-testid="why-blk-packs">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
            <Shield className="brand-gradient-text" size={22} />
          </div>
          <div>
            <GradientTitle size="md">LICENSED OWNERSHIP · OPERATED END-TO-END</GradientTitle>
            <p className="brand-gradient-text/60 text-[10px] font-black tracking-[0.22em] uppercase mt-0.5">You own the IP · we operate the platform</p>
          </div>
        </div>
        <p className="text-gray-400 text-sm leading-relaxed mb-5">
          Every drop is an <span className="text-white font-semibold">officially licensed product line</span> your legal team controls and your brand profits from. BLK PACKS handles production, fulfillment, payment processing, compliance, and customer service at an enterprise-SaaS standard — you keep your likeness, catalog rights, and renewal terms.
        </p>
        <InclusionGrid accent="gray" />
      </div>

      {/* ═══ TIERS ═══ */}
      <div className="text-center mb-6">
        <GradientTitle size="lg">PARTNERSHIP PACKAGES</GradientTitle>
        <p className="text-gray-500 text-xs md:text-sm mt-2 max-w-xl mx-auto">
          Four levels of activation. Every tier ships with full platform inclusions — fulfillment, processor guarantees, export data, account management, and EMERGE ledger coverage.
        </p>
      </div>
      <div className="space-y-8">
        <SilverTier />
        <GoldTier />
        <ARSilverTier />
        <ARGoldTier />
      </div>

      {/* ═══ FINAL CTA ═══ */}
      <div className="mt-14 text-center">
        <p className="text-gray-500 text-xs mb-4 max-w-lg mx-auto leading-relaxed">
          Revenue share wired NET 60 after each drop sells out — with guaranteed processor clearance and full export data delivered to your finance team.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={PARTNERSHIP_EMAIL + '?subject=Start%20a%20Partnership'}
            className="brand-gradient-btn font-black px-8 py-3 rounded-full text-sm hover:shadow-lg hover:shadow-orange-500/40"
            data-testid="partnership-contact-btn"
          >
            Start a Partnership <ArrowRight size={16} />
          </a>
          <a
            href={PARTNERSHIP_EMAIL + '?subject=Partnership%20Deck%20Request'}
            className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/15 text-white font-bold px-6 py-3 rounded-full text-sm hover:bg-white/[0.08] transition-all"
          >
            <Radio size={14} /> Request the Deck
          </a>
        </div>
      </div>
    </div>
  </div>
);

export default PartnershipsPage;
